function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
var dispatchCustomEvent = function dispatchCustomEvent(eventName) {
  var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var detail = {
    detail: data
  };
  var event = new CustomEvent(eventName, data ? detail : null);
  document.dispatchEvent(event);
};
/**
 * Publish a Shopify standard event (shopify:* DOM events for analytics apps).
 * The event classes are loaded async from Shopify's CDN via a module script in
 * theme.liquid — no-op until the module is available, and never let an
 * analytics payload error break a commerce flow.
 * https://shopify.dev/docs/storefronts/themes/best-practices/standard-events
 */
var publishStandardEvent = function publishStandardEvent(eventName, payload, target) {
  const SE = window.Shopify && window.Shopify.StandardEvents;
  if (!SE || !SE[eventName]) return;
  try {
    (target || document).dispatchEvent(new SE[eventName](payload || {}));
  } catch (e) {
    console.error('Standard event failed:', eventName, e);
  }
};
/**
 * Run a DOM update inside a View Transition when the browser supports it
 * (and the user hasn't asked for reduced motion) — otherwise apply the
 * update directly. Progressive enhancement only; callers must not rely
 * on the transition happening.
 *
 * Skipped while an overlay is open: the transition layer paints above
 * ALL z-indexes, so a named element (eg. the product grid) would flash
 * in front of the click-capture dim and any open drawer.
 */
var withViewTransition = function withViewTransition(update) {
  if (
    !document.startViewTransition ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    OverlayManager.hasOpen()
  ) {
    update();
    return;
  }
  document.startViewTransition(update);
};
window.recentlyViewedIds = [];

if (!window.DROPCartUI) {
  window.DROPCartUI = {
    queue: Promise.resolve(),
    pending: 0,
    revision: 0,
    sections() {
      return [
        { id: 'Cart', section: document.getElementById('Cart')?.dataset.id || 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ].filter((section) => document.getElementById(section.id));
    },
    strings() {
      return window.theme?.variantStrings || {};
    },
    cartUrl() {
      return theme.routes.cart_url;
    },
    sectionUrl() {
      const url = new URL(this.cartUrl(), window.location.origin);
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      url.searchParams.set('sections', this.sections().map((section) => section.section).join(','));
      return url.pathname + url.search;
    },
    async loadSections(controller = new AbortController()) {
      const url = new URL(this.sectionUrl(), window.location.origin);
      const sections = {};
      try {
        const result = await this.json(url.pathname + url.search, { headers: { Accept: 'application/json' }, cache: 'no-store' }, controller);
        if (result.response.ok && result.data && typeof result.data === 'object' && !result.data.errors) Object.assign(sections, result.data);
      } catch (error) {
        if (controller.signal.aborted || error.name === 'AbortError') throw error;
      }
      if (controller.signal.aborted) throw new DOMException('Cart refresh cancelled', 'AbortError');
      const missing = this.sections().filter((section) => this.innerHTML(sections[section.section], section.selector) === null);
      await Promise.all(missing.map(async (section) => {
        const retryUrl = new URL(url);
        retryUrl.searchParams.delete('sections');
        retryUrl.searchParams.set('section_id', section.section);
        try {
          const result = await this.request(retryUrl.pathname + retryUrl.search, { headers: { Accept: 'text/html' }, cache: 'no-store' }, controller, 'text');
          if (result.response.ok && this.innerHTML(result.data, section.selector) !== null) sections[section.section] = result.data;
        } catch (error) {
          if (controller.signal.aborted || error.name === 'AbortError') throw error;
        }
      }));
      const badge = this.sections().find((section) => section.id === 'cart-drawer-toggle');
      if (badge && this.innerHTML(sections[badge.section], badge.selector) === null) {
        const cartUrl = new URL(this.cartUrl(), window.location.origin);
        cartUrl.pathname = cartUrl.pathname.replace(/\/$/, '').replace(/\.js$/, '') + '.js';
        try {
          const result = await this.json(cartUrl.pathname + cartUrl.search, { headers: { Accept: 'application/json' }, cache: 'no-store' }, controller);
          if (result.response.ok && this.isCart(result.data)) {
            const count = result.data.item_count;
            sections[badge.section] = `<span class="thb-item-count">${count > 99 ? '99+' : count}</span>`;
          }
        } catch (error) {
          if (controller.signal.aborted || error.name === 'AbortError') throw error;
        }
      }
      if (controller.signal.aborted) throw new DOMException('Cart refresh cancelled', 'AbortError');
      return sections;
    },
    errorText(data, fallback) {
      for (const value of [data?.description, data?.errors, data?.message]) {
        if (typeof value === 'string' && value.trim()) return value;
        if (value && typeof value === 'object') {
          const parts = Object.values(value).flat().filter((part) => typeof part === 'string');
          if (parts.length) return parts.join(' ');
        }
      }
      return fallback;
    },
    message(container, text = '', success = false) {
      if (!container?.isConnected) return;
      let region = container.querySelector('[data-drop-cart-message]');
      if (!region && !text) return;
      if (!region) {
        region = document.createElement('div');
        region.setAttribute('data-drop-cart-message', '');
        region.className = 'form-notification';
        const host = container.querySelector('.side-panel-content, .product-card-info') || container;
        host.prepend(region);
      }
      region.textContent = '';
      region.setAttribute('role', success ? 'status' : 'alert');
      region.classList.toggle('error', !success);
      region.classList.toggle('success', success);
      region.toggleAttribute('hidden', !text);
      if (text) {
        const content = document.createElement('span');
        content.textContent = text + ' ';
        const link = document.createElement('a');
        link.href = this.cartUrl();
        link.textContent = this.strings().viewCart || 'View cart';
        content.append(link);
        region.append(content);
      }
    },
    json(url, options = {}, controller = new AbortController()) {
      return this.request(url, options, controller);
    },
    async request(url, options = {}, controller = new AbortController(), format = 'json') {
      let timer;
      try {
        return await Promise.race([
          (async () => {
            const response = await fetch(url, { ...options, signal: controller.signal });
            const data = await response[format]();
            return { response, data };
          })(),
          new Promise((resolve, reject) => {
            timer = setTimeout(() => {
              controller.abort();
              reject(new Error('Cart request timed out'));
            }, 20000);
          })
        ]);
      } finally {
        clearTimeout(timer);
      }
    },
    enqueue(task) {
      this.pending++;
      this.revision++;
      dispatchCustomEvent('drop:cart:write');
      const job = this.queue.then(task);
      const settled = job.finally(() => {
        this.pending--;
        this.revision++;
        if (!this.pending) dispatchCustomEvent('drop:cart:idle');
      });
      this.queue = settled.catch(() => {});
      return settled;
    },
    innerHTML(html, selector) {
      if (typeof html !== 'string' || !html.trim()) return null;
      const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return element ? element.innerHTML : null;
    },
    render(sections, allowMissingBadge = false) {
      const updates = [];
      for (const section of this.sections()) {
        const container = document.getElementById(section.id);
        if (!container) continue;
        const html = this.innerHTML(sections?.[section.section], section.selector);
        if (html === null) {
          if (allowMissingBadge && section.id === 'cart-drawer-toggle') continue;
          return false;
        }
        updates.push({ destination: container.querySelector(section.selector) || container, html });
      }
      if (!updates.length) return false;
      const active = document.activeElement;
      const focusRoot = active?.closest('#Cart-Drawer, #Cart');
      const focusId = active?.id;
      updates.forEach(({ destination, html }) => { destination.innerHTML = html; });
      if (focusRoot && !active.isConnected) {
        const root = document.getElementById(focusRoot.id);
        const next = Array.from(root?.querySelectorAll('[id]') || []).find((element) => focusId && element.id === focusId);
        (next || root?.querySelector('button, a[href], input'))?.focus({ preventScroll: true });
      }
      return true;
    },
    isCart(data) {
      return !!data && Array.isArray(data.items) && Number.isInteger(data.item_count) && data.item_count >= 0;
    },
    resetQuantities(container, cart) {
      container.querySelectorAll('quantity-selector[data-id]').forEach((selector) => {
        const input = selector.querySelector('.qty');
        if (!input) return;
        const item = cart?.items?.find((line) => line.key === selector.dataset.id);
        const quantity = item ? item.quantity : input.getAttribute('value');
        if (quantity !== null) input.value = String(quantity);
        if (typeof selector.validateQtyRules === 'function') selector.validateQtyRules();
      });
    }
  };
}



// --- Shopify standard events: adapters over the theme's internal event bus ---
(function () {
  const SE = () => window.Shopify && window.Shopify.StandardEvents;
  const ajaxCart = (cart) => SE().CartLinesUpdateEvent.createCartFromAjaxResponse(cart);

  // shopify:cart:view — cart drawer opened. The drawer HTML carries no cart
  // JSON, so fetch /cart.js (tiny, user-triggered) for an accurate payload.
  document.addEventListener('cart-drawer:open', () => {
    if (!SE()) return;
    fetch(`${theme.routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        if (!cart || !cart.token) return;
        publishStandardEvent('CartViewEvent', {
          cart: ajaxCart(cart),
          context: 'dialog'
        });
      })
      .catch(() => { /* analytics only — never surface */ });
  });

  // shopify:cart:lines-update (add) — /cart/add.js responds with the added
  // line(s) only, so the promise resolves without a full cart payload.
  document.addEventListener('cart:item-added', (e) => {
    const item = e.detail && e.detail.product;
    publishStandardEvent('CartLinesUpdateEvent', {
      action: 'add',
      lines: item && item.variant_id ? [{ merchandiseId: item.variant_id, quantity: item.quantity || 1 }] : undefined,
      promise: Promise.resolve({ cart: null })
    });
  });

  // shopify:cart:lines-update (update/remove) — /cart/change.js responds with
  // the full AJAX cart, already present in the internal event detail.
  document.addEventListener('line-item:change:end', (e) => {
    if (!SE()) return;
    const cart = e.detail && e.detail.cart;
    const quantity = e.detail ? parseInt(e.detail.quantity, 10) : NaN;
    publishStandardEvent('CartLinesUpdateEvent', {
      action: quantity === 0 ? 'remove' : 'update',
      promise: Promise.resolve({
        cart: cart && cart.token && cart.items ? ajaxCart(cart) : null
      })
    });
  });

  // shopify:cart:error — add-to-cart rejected (out of stock, limits, etc.)
  document.addEventListener('product:variant-error', (e) => {
    const detail = e.detail || {};
    publishStandardEvent('CartErrorEvent', {
      errors: [{ message: detail.message || detail.errors || 'Cart error' }]
    });
  });
})();

const OverlayManager = {
  stack: [],
  beforeCloseHandlers: new Map(),
  returnFocus: new WeakMap(),

  open(panel) {
    if (!panel || this.stack.includes(panel)) return;
    this.stack.push(panel);
    this.returnFocus.set(panel, document.activeElement);
    panel.classList.add('active');
    panel.inert = false;
    document.body.classList.add('open-cc');
    panel.querySelector('.side-panel-close')?.focus();
    dispatchCustomEvent('panel:open', { panel });
  },

  close(panel) {
    if (!panel) return;
    const handler = this.beforeCloseHandlers.get(panel);
    if (handler) {
      handler(() => this._close(panel));
    } else {
      this._close(panel);
    }
  },

  _close(panel) {
    const index = this.stack.indexOf(panel);
    if (index > -1) this.stack.splice(index, 1);
    panel.classList.remove('active');
    panel.inert = true;
    if (!this.stack.length) {
      document.body.classList.remove('open-cc');
    }
    // Return focus to the element that opened the overlay — but only when
    // focus is still inside the panel (or lost to <body>), so we never
    // steal focus the user has already moved elsewhere.
    const opener = this.returnFocus.get(panel);
    if (opener && document.contains(opener) && (panel.contains(document.activeElement) || document.activeElement === document.body)) {
      opener.focus();
    }
    this.returnFocus.delete(panel);
    dispatchCustomEvent('panel:closed', { panel });
  },

  closeLast() {
    const panel = this.stack[this.stack.length - 1];
    if (panel) this.close(panel);
  },

  registerBeforeClose(panel, handler) {
    this.beforeCloseHandlers.set(panel, handler);
  },

  isOpen(panel) {
    return this.stack.includes(panel);
  },

  hasOpen() {
    return this.stack.length > 0;
  },

  // Keep Tab/Shift+Tab cycling inside the top-most open overlay. One
  // document-level listener covers every drawer/modal the manager opens.
  trapFocus(e) {
    if (e.key !== 'Tab' || !this.stack.length) return;
    const panel = this.stack[this.stack.length - 1];
    const focusables = Array.from(panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"])'
    )).filter((el) => el.getClientRects().length > 0);

    if (!focusables.length) {
      e.preventDefault();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (!panel.contains(active)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }
};
document.addEventListener('keydown', (e) => OverlayManager.trapFocus(e));

/**
 *  @class
 *  @function Quantity
 */
if (!customElements.get('quantity-selector')) {
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.input = this.querySelector('.qty');
      this.subtract = this.querySelector('.minus');
      this.add = this.querySelector('.plus');
      if (!this.input || !this.subtract || !this.add) return;
      const step = Number(this.input.getAttribute('step'));
      this.step = Number.isFinite(step) && step > 0 ? step : 1;
      this.onMinus = this.onMinus || (() => this.change_quantity(-this.step));
      this.onPlus = this.onPlus || (() => this.change_quantity(this.step));
      this.onInputChange = this.onInputChange || (() => this.validateQtyRules());
      this.subtract.addEventListener('click', this.onMinus);
      this.add.addEventListener('click', this.onPlus);
      this.input.addEventListener('change', this.onInputChange);
      this.classList.add('buttons_added');
      this.validateQtyRules();
    }

    disconnectedCallback() {
      this.subtract?.removeEventListener('click', this.onMinus);
      this.add?.removeEventListener('click', this.onPlus);
      this.input?.removeEventListener('change', this.onInputChange);
    }

    change_quantity(change) {
      if (!this.input || this.input.disabled) return;
      const min = Number(this.input.getAttribute('min') || 1);
      const maximum = this.input.getAttribute('max');
      const current = this.input.value.trim() === '' ? min : Number(this.input.value);
      const value = (Number.isFinite(current) ? current : min) + change;
      if (value < min || (maximum !== null && maximum !== '' && value > Number(maximum))) return;
      this.input.value = String(value);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.validateQtyRules();
    }

    validateQtyRules() {
      if (!this.input || !this.subtract || !this.add) return;
      const value = Number(this.input.value);
      const min = Number(this.input.getAttribute('min') || 1);
      const maximum = this.input.getAttribute('max');
      const atMin = Number.isFinite(value) && value - this.step < min;
      const atMax = maximum !== null && maximum !== '' && Number.isFinite(value) && value + this.step > Number(maximum);
      this.subtract.classList.toggle('disabled', atMin);
      this.add.classList.toggle('disabled', atMax);
      this.subtract.disabled = this.input.disabled || atMin;
      this.add.disabled = this.input.disabled || atMax;
    }
  }
  customElements.define('quantity-selector', QuantityInput);
}

/**
 *  @class
 *  @function ProductCard
 */
if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this._observer = null;
    }
    connectedCallback() {
      this.swatches = this.querySelector('.product-card-swatches');
      this.image = this.querySelector('.product-card--featured-image-link .product-primary-image');
      this.additional_images = this.querySelectorAll('.product-secondary-image');
      this.additional_images_nav = this.querySelectorAll('.product-secondary-images-nav li');
      this.quick_add = this.querySelector('.product-card--add-to-cart-button-simple');
      this.size_options = this.querySelector('.product-card-sizes');

      if (this.swatches) {
        this.enableSwatches(this.swatches, this.image);
      }
      if (this.additional_images) {
        this.enableAdditionalImages();
      }
      if (this.quick_add) {
        this.quickAddHandler = this.quickAddHandler || this.quickAdd.bind(this);
        this.quick_add.removeEventListener('click', this.quickAddHandler);
        this.quick_add.addEventListener('click', this.quickAddHandler);
      }
      if (this.size_options) {
        this.enableSizeOptions();
      }
      this.observeVideo();
    }
    disconnectedCallback() {
      this.quick_add?.removeEventListener('click', this.quickAddHandler);
      this.sizeListeners?.forEach((handlers, size) => {
        size.removeEventListener('click', handlers.click);
        size.removeEventListener('keydown', handlers.keydown);
      });
      this.sizeListeners?.clear();
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }
    observeVideo() {
      var template = this.querySelector('.product-card__video-deferred');
      if (!template) return;

      var self = this;
      this._observer = new IntersectionObserver(function(entries) {
        if (entries[0].isIntersecting) {
          self._observer.disconnect();
          self._observer = null;
          template.replaceWith(template.content.cloneNode(true));
        }
      }, { rootMargin: '200px' });

      this._observer.observe(this);
    }
    enableAdditionalImages() {
      let image_length = this.additional_images.length;
      let images = this.additional_images;
      let nav = this.additional_images_nav;
      let hasNav = nav.length > 0;
      let image_container = this.querySelector('.product-card--featured-image-link');
      let lastIndex = -1;

      const mousemove = function (e) {
        let l = e.offsetX;
        let w = this.getBoundingClientRect().width;
        let sel = Math.floor((l / w) * image_length);

        if (sel === lastIndex || !images[sel]) {
          return;
        }
        if (lastIndex >= 0 && images[lastIndex]) {
          images[lastIndex].classList.remove('hover');
          if (hasNav) {
            nav[lastIndex].classList.remove('active');
          }
        }
        images[sel].classList.add('hover');
        if (hasNav) {
          nav[sel].classList.add('active');
        }
        lastIndex = sel;
      };

      const mouseleave = function () {
        if (lastIndex >= 0 && images[lastIndex]) {
          images[lastIndex].classList.remove('hover');
          if (hasNav) {
            nav[lastIndex].classList.remove('active');
          }
        }
        lastIndex = -1;
      };

      if (image_container) {
        image_container.addEventListener('touchstart', mousemove, {
          passive: true
        });
        image_container.addEventListener('touchmove', mousemove, {
          passive: true
        });
        image_container.addEventListener('touchend', mouseleave, {
          passive: true
        });
        image_container.addEventListener('mouseenter', mousemove, {
          passive: true
        });
        image_container.addEventListener('mousemove', mousemove, {
          passive: true
        });
        image_container.addEventListener('mouseleave', mouseleave, {
          passive: true
        });
      }

      window.addEventListener('load', () => {
        images.forEach((image) => {
          lazySizes.loader.unveil(image);
        });
      });

    }
    enableSwatches(swatches, image) {
      let swatch_list = swatches.querySelectorAll('.product-card-swatch'),
        org_srcset = image ? image.dataset.srcset : '';
      this.color_index = this.swatches.dataset.index;

      window.addEventListener('load', () => {
        swatch_list.forEach((swatch) => {
          let img = new Image();
          img.srcset = swatch.dataset.srcset;
          lazySizes.loader.unveil(img);
        });
      });

      swatch_list.forEach((swatch, index) => {
        swatch.addEventListener('mouseover', () => {
          swatch_list.forEach((el) => {
            el.classList.remove('active');
          });
          if (image) {
            if (swatch.dataset.srcset) {
              image.setAttribute('srcset', swatch.dataset.srcset);
            } else {
              image.setAttribute('srcset', org_srcset);
            }
          }
          if (this.size_options) {
            this.current_options[this.color_index] = swatch.querySelector('span').innerText;
            this.updateMasterId();
          }
          swatch.classList.add('active');
        });
        swatch.addEventListener('click', function (evt) {
          window.location.href = this.dataset.href;
          evt.preventDefault();
        });
      });
    }
    enableSizeOptions() {
      let size_list = this.size_options.querySelectorAll('.product-card-sizes--size'),
        featured_image = this.querySelector('.product-card--featured-image'),
        has_hover = featured_image.classList.contains('thb-hover'),
        size_parent = this.size_options.parentElement;

      this.size_index = this.size_options.dataset.index;

      this.current_options = this.size_options.dataset.options.split(',');

      this.updateMasterId();

      size_parent.addEventListener('mouseenter', () => {
        if (has_hover) {
          featured_image.classList.remove('thb-hover');
        }
      }, {
        passive: true
      });
      size_parent.addEventListener('mouseleave', () => {
        if (has_hover) {
          featured_image.classList.add('thb-hover');
        }
      }, {
        passive: true
      });
      if (!this.sizeListeners) this.sizeListeners = new Map();
      size_list.forEach((size) => {
        const previous = this.sizeListeners.get(size);
        if (previous) {
          size.removeEventListener('click', previous.click);
          size.removeEventListener('keydown', previous.keydown);
        }
        const click = (event) => {
          event.preventDefault();
          if (this.addRequest || size.classList.contains('is-disabled')) return;
          this.current_options[this.size_index] = size.querySelector('span')?.textContent.trim();
          this.updateMasterId();
          if (!this.currentVariant?.available) {
            window.DROPCartUI.message(this, window.DROPCartUI.strings().unavailable || 'Unavailable');
            return;
          }
          this.addToCart(this.currentVariant.id, size);
        };
        const keydown = (event) => {
          if (event.key === 'Enter' || event.key === ' ') click(event);
        };
        size.setAttribute('role', 'button');
        size.addEventListener('click', click);
        size.addEventListener('keydown', keydown);
        this.sizeListeners.set(size, { click, keydown });
      });
      this.syncSizeAccessibility();
    }
    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.current_options[index] === option;
        }).includes(false);
      });
      setTimeout(() => {
        this.setDisabled();
      }, 100);
    }
    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }
    setDisabled() {
      const variant_data = this.getVariantData();
      if (!variant_data) {
        return true;
      }
      const size_buttons = this.size_options.querySelectorAll('.product-card-sizes--size');

      if (this.currentVariant) {
        const selected_options = this.currentVariant.options.map((value, index) => ({
          value,
          index: `option${index + 1}`
        }));
        const available_options = this.createAvailableOptionsTree(variant_data, selected_options);
        const fieldset_options = Object.values(available_options)[this.size_index];
        if (fieldset_options) {
          size_buttons.forEach((input, input_i) => {
            input.classList.toggle('is-disabled', fieldset_options[input_i]?.isUnavailable !== false);
          });
        }
      } else {
        size_buttons.forEach((input) => {
          input.classList.add('is-disabled');
        });
      }
      this.syncSizeAccessibility();
      return true;
    }
    createAvailableOptionsTree(variant_data, selected_options) {
      // Reduce variant array into option availability tree
      return variant_data.reduce((options, variant) => {

        // Check each option group (e.g. option1, option2, option3) of the variant
        Object.keys(options).forEach(index => {

          if (variant[index] === null) return;

          let entry = options[index].find(option => option.value === variant[index]);

          if (typeof entry === 'undefined') {
            // If option has yet to be added to the options tree, add it
            entry = {
              value: variant[index],
              isUnavailable: true
            };
            options[index].push(entry);
          }

          // Check how many selected option values match a variant
          const countVariantOptionsThatMatchCurrent = selected_options.reduce((count, {
            value,
            index
          }) => {
            return variant[index] === value ? count + 1 : count;
          }, 0);

          // Only enable an option if an available variant matches all but one current selected value
          if (countVariantOptionsThatMatchCurrent >= selected_options.length - 1) {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }

          // Make sure if a variant is unavailable, disable currently selected option
          if ((!this.currentVariant || !this.currentVariant.available) && selected_options.find((option) => option.value === entry.value && index === option.index)) {
            entry.isUnavailable = true;
          }

          // First option is always enabled
          if (index === 'option1') {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }
        });

        return options;
      }, {
        option1: [],
        option2: [],
        option3: []
      });
    }
    syncSizeAccessibility() {
      this.querySelectorAll('.product-card-sizes--size').forEach((size) => {
        const disabled = !!this.addRequest || size.classList.contains('is-disabled');
        size.setAttribute('aria-disabled', String(disabled));
        size.setAttribute('tabindex', disabled ? '-1' : '0');
      });
    }

    setQuickAddBusy(busy, request) {
      if (busy) {
        request.buttons = Array.from(this.querySelectorAll('.product-card--add-to-cart-button-simple')).map((button) => ({ button, disabled: button.disabled, aria: button.getAttribute('aria-disabled') }));
        request.buttons.forEach(({ button }) => { button.disabled = true; button.setAttribute('aria-disabled', 'true'); });
        request.button.classList.add('loading');
        request.button.setAttribute('aria-disabled', 'true');
      } else {
        request.button.classList.remove('loading');
        request.buttons.forEach(({ button, disabled, aria }) => {
          button.disabled = disabled;
          if (aria === null) button.removeAttribute('aria-disabled');
          else button.setAttribute('aria-disabled', aria);
        });
        if (!request.buttons.some(({ button }) => button === request.button)) request.button.removeAttribute('aria-disabled');
      }
      this.syncSizeAccessibility();
    }

    addToCart(variantId, button) {
      const ui = window.DROPCartUI;
      const id = Number(variantId);
      if (this.addRequest || !button || button.disabled || button.classList.contains('is-disabled')) return Promise.resolve(false);
      if (!Number.isSafeInteger(id) || id <= 0) {
        ui.message(this, ui.strings().unavailable || 'Unavailable');
        return Promise.resolve(false);
      }
      const request = { button };
      this.addRequest = request;
      this.setQuickAddBusy(true, request);
      ui.message(this);
      request.promise = ui.enqueue(async () => {
        let accepted = false;
        try {
          if (!this.isConnected) return false;
          const formData = new FormData();
          formData.append('id', id);
          formData.append('quantity', 1);
          formData.append('sections', ui.sections().map((section) => section.section).join(','));
          formData.append('sections_url', window.location.pathname);
          const { response, data } = await ui.json(theme.routes.cart_add_url, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: formData });
          if (!response.ok || data?.status || data?.errors) {
            const message = ui.errorText(data, ui.strings().cartRequestError || 'We could not confirm the cart update.');
            ui.message(this, message + ' ' + (ui.strings().cartCheckAfterError || 'Check your cart before trying again.'));
            dispatchCustomEvent('product:variant-error', { source: 'product-card', productVariantId: String(id), errors: data?.errors || message, message });
            dispatchCustomEvent('cart:refresh');
            return false;
          }
          const item = Array.isArray(data?.items) ? data.items[0] : data;
          if (!Number.isSafeInteger(Number(item?.variant_id || item?.id)) || Number(item.variant_id || item.id) <= 0) throw new Error('Missing added item');
          accepted = true;
          let rendered = false;
          try {
            rendered = this.renderContents(data);
          } catch (error) {
            console.error(error);
          }
          dispatchCustomEvent('cart:item-added', { product: item, source: 'product-card' });
          if (!rendered) {
            ui.message(this, ui.strings().cartAddedRefreshError || 'Item added. Open your cart to confirm its latest contents before adding it again.', true);
            dispatchCustomEvent('cart:refresh');
          }
          return true;
        } catch (error) {
          console.error(error);
          ui.message(this, accepted ? ui.strings().cartAddedRefreshError || 'Item added. Open your cart to confirm its latest contents before adding it again.' : ui.strings().cartUnconfirmedError || 'We could not confirm the cart update. Check your cart before trying again.', accepted);
          dispatchCustomEvent('cart:refresh');
          return false;
        } finally {
          if (this.addRequest === request) this.addRequest = null;
          this.setQuickAddBusy(false, request);
        }
      });
      return request.promise;
    }

    quickAdd(event) {
      event.preventDefault();
      if (!this.quick_add || this.quick_add.disabled || this.addRequest) return;
      return this.addToCart(this.quick_add.dataset.productId, this.quick_add);
    }

    getSectionsToRender() {
      return window.DROPCartUI.sections();
    }

    renderContents(state) {
      if (!window.DROPCartUI.render(state.sections)) return false;
      const drawer = document.getElementById('Cart-Drawer');
      if (drawer) {
        OverlayManager.open(drawer);
        dispatchCustomEvent('cart-drawer:open');
      }
      return true;
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return window.DROPCartUI.innerHTML(html, selector);
    }
  }
  customElements.define('product-card', ProductCard);
}


/**
 *  @class
 *  @function PanelClose
 */
if (!customElements.get('side-panel-close')) {
  class PanelClose extends HTMLElement {
    connectedCallback() {
      this.cc = document.querySelector('.click-capture');

      this.onClick = () => {
        OverlayManager.closeLast();
      };

      this.addEventListener('click', this.onClick);

      if (!this.cc.hasAttribute('initialized')) {
        this.cc.addEventListener('click', this.onClick);
        this.cc.setAttribute('initialized', '');
      }
    }
  }
  customElements.define('side-panel-close', PanelClose);

  // Product drawer: close images container first, then drawer
  const productDrawer = document.getElementById('Product-Drawer');
  if (productDrawer) {
    OverlayManager.registerBeforeClose(productDrawer, (done) => {
      const container = productDrawer.querySelector('.product-quick-images--container');
      if (container && container.classList.contains('active') && window.innerWidth >= 1069) {
        container.classList.remove('active');
        container.addEventListener('transitionend', () => {
          productDrawer.querySelector('#Product-Drawer-Content').innerHTML = '';
          done();
        }, { once: true });
      } else {
        container?.classList.remove('active');
        productDrawer.querySelector('#Product-Drawer-Content').innerHTML = '';
        done();
      }
    });

    document.addEventListener('panel:open', (e) => {
      if (e.detail.panel === productDrawer) {
        setTimeout(() => {
          productDrawer.querySelector('.product-quick-images--container')?.classList.add('active');
        });
      }
    });
  }

  document.addEventListener('keyup', (e) => {
    if (e.code?.toUpperCase() === 'ESCAPE') {
      OverlayManager.closeLast();
    }
  });
}
/**
 *  @class
 *  @function CartDrawer
 */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.changeTimers = this.changeTimers || new Map();
      this.changes = this.changes || new Map();
      this.revision = this.revision || 0;
      this.onToggle = this.onToggle || ((event) => {
        event.preventDefault();
        OverlayManager.open(this);
        dispatchCustomEvent('cart-drawer:open');
      });
      this.onPanelOpen = this.onPanelOpen || ((event) => {
        if (event.detail.panel !== this) return;
        setTimeout(() => {
          if (this.isConnected && OverlayManager.isOpen(this)) this.querySelector('.product-recommendations--full')?.classList.add('active');
        });
      });
      this.onRefresh = this.onRefresh || (() => this.refresh());
      this.onWrite = this.onWrite || (() => this.invalidateRefresh());
      this.onCartAdded = this.onCartAdded || ((event) => {
        this.invalidateRefresh();
        if (event.detail?.source === 'product-card') return;
        this.refreshWanted = true;
        Promise.resolve().then(() => { if (this.refreshWanted && this.isConnected) this.refresh(); });
      });
      this.onIdle = this.onIdle || (() => {
        if (this.refreshWanted && !this.processing) { this.refreshWanted = false; this.refresh(); }
      });
      this.onExternalChange = this.onExternalChange || ((event) => {
        if (event.detail?.source === 'cart-drawer' || !window.DROPCartUI.isCart(event.detail?.cart)) return;
        this.refresh();
      });
      this.changeHandler = this.changeHandler || this.onChange.bind(this);
      this.onSubmit = this.onSubmit || ((event) => {
        if (this.processing || this.refreshRequest || this.changeTimers.size || window.DROPCartUI.pending) {
          event.preventDefault();
          window.DROPCartUI.message(this, 'Please wait for your cart update to finish before checking out.');
        }
      });
      this.toggleButton?.removeEventListener('click', this.onToggle);
      this.toggleButton = document.getElementById('cart-drawer-toggle');
      this.toggleButton?.removeEventListener('click', this.onToggle);
      this.toggleButton?.addEventListener('click', this.onToggle);
      const listeners = [ ['panel:open', this.onPanelOpen], ['cart:refresh', this.onRefresh], ['drop:cart:write', this.onWrite], ['drop:cart:idle', this.onIdle], ['cart:item-added', this.onCartAdded], ['line-item:change:end', this.onExternalChange] ];
      listeners.forEach(([name, handler]) => { document.removeEventListener(name, handler); document.addEventListener(name, handler); });
      this.removeEventListener('change', this.changeHandler);
      this.addEventListener('change', this.changeHandler);
      this.removeEventListener('submit', this.onSubmit, true);
      this.addEventListener('submit', this.onSubmit, true);
      OverlayManager.registerBeforeClose(this, (done) => {
        const recs = this.querySelector('.product-recommendations--full');
        if (recs && recs.classList.contains('active') && window.innerWidth >= 1069) {
          recs.classList.remove('active');
          let finished = false;
          let timer;
          const finish = () => {
            if (finished) return;
            finished = true;
            clearTimeout(timer);
            recs.removeEventListener('transitionend', finish);
            done();
          };
          recs.addEventListener('transitionend', finish);
          timer = setTimeout(finish, 400);
        } else {
          recs?.classList.remove('active');
          done();
        }
      });
    }

    disconnectedCallback() {
      this.toggleButton?.removeEventListener('click', this.onToggle);
      [['panel:open', this.onPanelOpen], ['cart:refresh', this.onRefresh], ['drop:cart:write', this.onWrite], ['drop:cart:idle', this.onIdle], ['cart:item-added', this.onCartAdded], ['line-item:change:end', this.onExternalChange]].forEach(([name, handler]) => document.removeEventListener(name, handler));
      this.removeEventListener('change', this.changeHandler);
      this.removeEventListener('submit', this.onSubmit, true);
      this.changeTimers?.forEach((timer) => clearTimeout(timer));
      this.changeTimers?.clear();
      this.changes?.clear();
      this.invalidateRefresh();
      OverlayManager.beforeCloseHandlers.delete(this);
    }

    invalidateRefresh() {
      this.revision++;
      this.refreshRequest?.controller.abort();
    }

    rowForKey(key) {
      return Array.from(this.querySelectorAll('.product-cart-item')).find((row) => row.querySelector('quantity-selector')?.dataset.id === key);
    }

    selection(line, quantity, key) {
      const row = key ? this.rowForKey(key) : Number.isInteger(Number(line)) ? this.querySelector(`#CartDrawerItem-${Number(line)}`) : null;
      const input = row?.querySelector('.qty');
      key = key || row?.querySelector('quantity-selector')?.dataset.id;
      const value = String(quantity).trim();
      const amount = value === '' ? NaN : Number(value);
      const min = Number(input?.getAttribute('min') || 0);
      const max = input?.getAttribute('max');
      const step = Number(input?.getAttribute('step') || 1);
      if (!key || !Number.isSafeInteger(amount) || amount < 0 || (amount !== 0 && (amount < min || (max !== null && max !== undefined && amount > Number(max)) || (step > 0 && (amount - min) % step !== 0)))) {
        if (row) window.DROPCartUI.resetQuantities(row);
        window.DROPCartUI.message(this, window.DROPCartUI.strings().cartQuantityError || 'Please choose an available quantity and try again.');
        return null;
      }
      return { key, quantity: amount, line, row };
    }

    onChange(event) {
      if (!event.target.classList.contains('qty')) return;
      const key = event.target.closest('quantity-selector')?.dataset.id;
      clearTimeout(this.changeTimers.get(key));
      this.changeTimers.delete(key);
      const selection = this.selection(event.target.dataset.index, event.target.value, key);
      if (!selection) return;
      clearTimeout(this.changeTimers.get(selection.key));
      this.changeTimers.set(selection.key, setTimeout(() => {
        this.changeTimers.delete(selection.key);
        this.updateQuantity(selection.line, selection.quantity, selection.key);
      }, 300));
    }

    getSectionsToRender() {
      return window.DROPCartUI.sections();
    }

    getSectionInnerHTML(html, selector) {
      return window.DROPCartUI.innerHTML(html, selector);
    }

    updateQuantity(line, quantity, key) {
      const selection = this.selection(line, quantity, key);
      if (!selection) return Promise.resolve(false);
      clearTimeout(this.changeTimers.get(selection.key));
      this.changeTimers.delete(selection.key);
      if (this.activeChange?.key === selection.key && this.activeChange.quantity === selection.quantity && !this.changes.has(selection.key)) return this.processing;
      this.changes.set(selection.key, selection);
      if (this.processing) return this.processing;
      this.processing = this.processChanges().finally(() => {
        this.processing = null;
        this.activeChange = null;
        this.querySelectorAll('.thb-loading').forEach((row) => row.classList.remove('thb-loading'));
        this.removeAttribute('aria-busy');
        if (this.isConnected && this.refreshWanted) { this.refreshWanted = false; this.refresh(); }
      });
      return this.processing;
    }

    async processChanges() {
      const ui = window.DROPCartUI;
      let success = true;
      while (this.changes.size && this.isConnected) {
        const [key, change] = this.changes.entries().next().value;
        this.changes.delete(key);
        this.activeChange = change;
        this.setAttribute('aria-busy', 'true');
        this.rowForKey(key)?.classList.add('thb-loading');
        this.querySelector('.product-recommendations--full')?.classList.remove('active');
        ui.message(this);
        const accepted = await ui.enqueue(async () => {
          if (!this.isConnected) return false;
          dispatchCustomEvent('line-item:change:start', { quantity: change.quantity, source: 'cart-drawer', key });
          let cart;
          try {
            const result = await ui.json(theme.routes.cart_change_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity: change.quantity, sections: ui.sections().map((section) => section.section), sections_url: window.location.pathname }) });
            if (!result.response.ok || result.data?.errors || result.data?.status) {
              const error = new Error(ui.errorText(result.data, ui.strings().cartRequestError || 'The cart could not be updated.'));
              error.confirmed = true;
              throw error;
            }
            if (!ui.isCart(result.data)) throw new Error('Missing cart state');
            cart = result.data;
            if (!this.isConnected) return true;
            let rendered = false;
            try {
              rendered = ui.render(cart.sections);
            } catch (error) {
              console.error(error);
            }
            dispatchCustomEvent('line-item:change:end', { quantity: change.quantity, cart, source: 'cart-drawer', key });
            if (!rendered) {
              const refreshed = await this.readSections();
              if (!refreshed) {
                ui.resetQuantities(this, cart);
                ui.message(this, ui.strings().cartUpdatedRefreshError || 'Your cart was updated, but its display could not refresh. Open your cart to check it.', true);
              }
            }
            return true;
          } catch (error) {
            console.error(error);
            if (!this.isConnected) return false;
            await this.readSections();
            ui.resetQuantities(this, cart);
            const message = error.confirmed ? error.message : ui.strings().cartUnconfirmedError || 'We could not confirm the cart update. Check your cart before trying again.';
            ui.message(this, message);
            dispatchCustomEvent('cart:update:error', { source: 'cart-drawer', key, message });
            return false;
          } finally {
            this.rowForKey(key)?.classList.remove('thb-loading');
          }
        });
        if (!accepted) {
          success = false;
          this.changes.clear();
          this.changeTimers.forEach((timer) => clearTimeout(timer));
          this.changeTimers.clear();
          break;
        }
      }
      return success;
    }

    async readSections() {
      if (!this.isConnected) return false;
      this.invalidateRefresh();
      const request = { revision: this.revision, cartRevision: window.DROPCartUI.revision, controller: new AbortController() };
      this.refreshRequest = request;
      try {
        const data = await window.DROPCartUI.loadSections(request.controller);
        if (!this.isConnected || this.refreshRequest !== request || this.revision !== request.revision || window.DROPCartUI.revision !== request.cartRevision) return false;
        if (!window.DROPCartUI.render(data, true)) throw new Error('Cart sections unavailable');
        dispatchCustomEvent('cart:updated', { source: 'cart-drawer-refresh' });
        return true;
      } catch (error) {
        if (this.refreshRequest === request && this.revision === request.revision && this.isConnected && error.name !== 'AbortError') {
          console.error(error);
          window.DROPCartUI.message(this, window.DROPCartUI.strings().cartRefreshError || 'The cart display could not refresh. Open your cart to check the latest contents.');
        }
        return false;
      } finally {
        if (this.refreshRequest === request) this.refreshRequest = null;
      }
    }

    async refresh() {
      if (!this.isConnected) return false;
      if (this.processing || window.DROPCartUI.pending || this.changeTimers?.size) { this.refreshWanted = true; return false; }
      this.refreshWanted = false;
      return this.readSections();
    }
  }
  customElements.define('cart-drawer', CartDrawer);
}

/**
 *  @class
 *  @function ResizeSelect
 */
if (!customElements.get('resize-select')) {
  class ResizeSelect extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.select = this.querySelector('select');
      this.addEventListeners();
    }
    addEventListeners() {
      this.addEventListener('change', this.resizeSelect.bind(this));
      window.addEventListener('load', this.resizeSelect.bind(this));
      const details = this.closest('details');
      if (details) {
        details.addEventListener('toggle', () => {
          if (details.open) this.resizeSelect();
        });
      }
      this.resizeSelect();
    }
    resizeSelect() {
      let tempOption = document.createElement('option');
      tempOption.textContent = this.select.selectedOptions[0].textContent;

      let tempSelect = document.createElement('select');
      tempSelect.style.cssText = 'visibility:hidden;position:fixed;pointer-events:none';
      tempSelect.appendChild(tempOption);
      this.after(tempSelect);

      requestAnimationFrame(() => {
        if (tempSelect.clientWidth > 0) {
          this.select.style.width = `${tempSelect.clientWidth + 10}px`;
        }
        tempSelect.remove();
      });
    }
  }
  customElements.define('resize-select', ResizeSelect);
}



/**
 *  @class
 *  @function QuickView
 */
if (!customElements.get('quick-view')) {
  const quickViewCache = new Map();

  class QuickView extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.drawer = document.getElementById('Product-Drawer');
      this.addEventListener('click', this.handleClick.bind(this));
    }
    handleClick(e) {
      e.preventDefault();
      let productHandle = this.dataset.productHandle;
      if (!productHandle) {
        return;
      }
      let rootUrl = theme.routes.root_url.replace(/\/+$/, '');
      let href = `${rootUrl}/products/${productHandle}?view=quick-view`;

      if (this.classList.contains('loading')) {
        return;
      }
      this.classList.add('loading');

      // Abort any in-flight quick-view fetch
      if (QuickView.activeController) {
        QuickView.activeController.abort();
      }
      let controller = new AbortController();
      QuickView.activeController = controller;

      // Serve from cache if available
      if (quickViewCache.has(productHandle)) {
        this.classList.remove('loading');
        this.renderQuickView(quickViewCache.get(productHandle), href, productHandle);
        return;
      }

      fetch(href, {
        method: 'GET',
        signal: controller.signal
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Quick view fetch failed: ${response.status}`);
          }
          return response.text();
        })
        .then(text => {
          let parsed = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#Product-Drawer-Content');

          if (!parsed) {
            throw new Error('Quick view content not found in response');
          }
          let sectionInnerHTML = parsed.innerHTML;
          quickViewCache.set(productHandle, sectionInnerHTML);
          this.renderQuickView(sectionInnerHTML, href, productHandle);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error(error);
          }
        })
        .finally(() => {
          this.classList.remove('loading');
        });
    }
    _reloadScripts(container) {
      let scripts = container.querySelectorAll('script');
      let head = document.head;
      scripts.forEach((oldScript) => {
        let newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        if (oldScript.src) {
          if (document.querySelector(`script[src="${oldScript.src}"]`)) {
            return;
          }
        } else {
          newScript.textContent = oldScript.textContent;
        }
        if (oldScript.parentNode) {
          oldScript.parentNode.replaceChild(newScript, oldScript);
        } else {
          head.appendChild(newScript);
        }
      });
    }
    renderQuickView(sectionInnerHTML, href, productHandle) {
      if (!sectionInnerHTML) {
        return;
      }
      let drawerContent = this.drawer.querySelector('#Product-Drawer-Content');
      drawerContent.innerHTML = sectionInnerHTML;

      this._reloadScripts(drawerContent);

      requestAnimationFrame(() => {
        if (typeof Shopify !== 'undefined' && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
      });

      OverlayManager.open(this.drawer);

      dispatchCustomEvent('quick-view:open', {
        productUrl: href,
        productHandle: productHandle
      });
      addIdToRecentlyViewed(productHandle);
    }
  }
  QuickView.activeController = null;
  customElements.define('quick-view', QuickView);
}

/**
 *  @class
 *  @function SidePanelContentTabs
 */
if (!customElements.get('side-panel-content-tabs')) {
  class SidePanelContentTabs extends HTMLElement {
    constructor() {
      super();
      this.buttons = this.querySelectorAll('button');
      this.panels = this.parentElement.querySelectorAll('.side-panel-content--tab-panel');
    }
    connectedCallback() {
      this.setupButtonObservers();
    }
    setupButtonObservers() {
      this.buttons.forEach((item, i) => {
        item.addEventListener('click', (e) => {
          this.toggleActiveClass(i);
        });
      });
    }
    toggleActiveClass(i) {
      this.buttons.forEach((button) => {
        button.classList.remove('tab-active');
      });
      this.buttons[i].classList.add('tab-active');

      this.panels.forEach((panel) => {
        panel.classList.remove('tab-active');
      });
      this.panels[i].classList.add('tab-active');
    }
  }

  customElements.define('side-panel-content-tabs', SidePanelContentTabs);
}

/**
 *  @class
 *  @function CollapsibleRow
 */
if (!customElements.get('collapsible-row')) {
  // https://css-tricks.com/how-to-animate-the-details-element/
  class CollapsibleRow extends HTMLElement {
    constructor() {
      super();

      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('.collapsible__content');

      // Store the animation object (so we can cancel it if needed)
      this.animation = null;
      // Store if the element is closing
      this.isClosing = false;
      // Store if the element is expanding
      this.isExpanding = false;
    }
    connectedCallback() {
      this.setListeners();
    }
    setListeners() {
      // Opt-out of the WAAPI height animation. Used by horizontal facet
      // dropdowns, where the panel is floating (position: absolute) and
      // animating the parent <details> height would push surrounding layout.
      if (this.dataset.skipCollapse === 'true') return;
      this.querySelector('summary').addEventListener('click', (e) => this.onClick(e));
    }
    onClick(e) {
      // Stop default behaviour from the browser
      e.preventDefault();
      // Add an overflow on the <details> to avoid content overflowing
      this.details.style.overflow = 'hidden';
      // Check if the element is being closed or is already closed
      if (this.isClosing || !this.details.open) {
        this.open();
        // Check if the element is being openned or is already open
      } else if (this.isExpanding || this.details.open) {
        this.shrink();
      }
    }
    shrink() {
      // Set the element as "being closed"
      this.isClosing = true;

      // Store the current height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the height of the summary
      const endHeight = `${this.summary.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 250,
        easing: 'ease'
      });

      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(false);
      // If the animation is cancelled, isClosing variable is set to false
      this.animation.oncancel = () => this.isClosing = false;
    }

    open() {
      // Apply a fixed height on the element
      this.details.style.height = `${this.details.offsetHeight}px`;
      // Force the [open] attribute on the details element
      this.details.open = true;
      // Wait for the next frame to call the expand function
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      // Set the element as "being expanding"
      this.isExpanding = true;
      // Get the current fixed height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the open height of the element (summary height + content height)
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 400,
        easing: 'ease-out'
      });
      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(true);
      // If the animation is cancelled, isExpanding variable is set to false
      this.animation.oncancel = () => this.isExpanding = false;
    }

    onAnimationFinish(open) {
      // Set the open attribute based on the parameter
      this.details.open = open;
      // Clear the stored animation
      this.animation = null;
      // Reset isClosing & isExpanding
      this.isClosing = false;
      this.isExpanding = false;
      // Remove the overflow hidden and the fixed height
      this.details.style.height = this.details.style.overflow = '';
    }
  }
  customElements.define('collapsible-row', CollapsibleRow);
}

/**
 *  @function addIdToRecentlyViewed
 */
function addIdToRecentlyViewed(handle) {

  if (!handle) {
    let product = document.querySelector('.thb-product-detail');

    if (product) {
      handle = product.dataset.handle;
    }
  }
  if (!handle) {
    return;
  }
  if (window.localStorage) {
    let recentIds = window.localStorage.getItem('recently-viewed');
    if (recentIds != 'undefined' && recentIds != null) {
      window.recentlyViewedIds = JSON.parse(recentIds);
    }
  }
  // Remove current product if already in recently viewed array
  var i = window.recentlyViewedIds.indexOf(handle);

  if (i > -1) {
    window.recentlyViewedIds.splice(i, 1);
  }

  // Add id to array
  window.recentlyViewedIds.unshift(handle);

  if (window.localStorage) {
    window.localStorage.setItem('recently-viewed', JSON.stringify(window.recentlyViewedIds));
  }
}

